import React, {useEffect, useState, useContext, useRef} from 'react';
import _ from "lodash";
import { Portal } from 'react-portal';

import Date from "../../../ui/Date";
import InlineInputControlRow from "../../../ui/layout/InlineInputControlRow";
import Label from '../../../ui/Label';
import PrimaryButton from '../../../ui/PrimaryButton';
import SecondaryButton from "../../../ui/SecondaryButton";
import Select from "../../../ui/Select";
import SmartTable from '../../../uiControls/SmartTable';
import TextBox from "../../../ui/TextBox";
import { NumberedIndicatorRenderer, TextRenderer, DateRenderer, DateTimeRenderer, NullableCheckboxIconRenderer } from "../../../uiControls/SmartTableRenderers";
import useTableStorage from '../../../hooks/useTableStorage';
import { useTypedFastStorage } from '../../../hooks/useFastStorage';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';

import Fieldset from '../../common/Fieldset';
import { formatDate, addYears, addMonths, addDays, compareIso, getDiffMonths, getCurrentYear } from '../../../dateUtils';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { ApplicableTableSettings } from '../../../tableSettings';
import { FiscalYearEnd } from "../../../fms.core/FiscalYearEnd";
import { FYPeriod } from "../../../fms.core/FYPeriod";
import { fiscalYearSettingsDetailsTable } from './yearEndProcessing/tableSettings';
import { hideGlobalOkDialog } from '../../../features/globalOkDialogSlice';
import { SQLField, SQLInfo, SQLType } from '../../../framework.core/data/SQLInfo';
import ApplicationContext from '../../../ApplicationContext';

const ProgressDialogStyle:React.CSSProperties = {
  position: 'fixed',
  inset: '0px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 9999999,
  display: 'none',
};

const ProgressDialogContainerStyle:React.CSSProperties = {
  position: 'absolute',
  inset: '50% auto auto 50%',
  border: '1px solid rgb(204, 204, 204)',
  background: 'rgb(255, 255, 255)',
  overflow: 'auto',
  borderRadius: '4px',
  outline: 'none',
  padding: '20px',
  marginRight: '-50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 9999999,
};

interface ManageFiscalYearEndParams {
  yearEnd: FiscalYearEnd,
  refreshCnt: number,
  setRefreshCnt: (x: number) => void,
  onPopupClose: () => void
};

interface ManageFiscalYearEndLoadedDetailsViewParams {
  yearEnd: FiscalYearEnd,
  setYearEnd: (x: Partial<FiscalYearEnd>) => void,
  delPeriodIds: Array<number>,
  setDelPeriodIds: (x: Array<number>) => void,
  savePeriods: () => void,
  onPopupClose: () => void
};

function ActionRenderer({data, row, cell}: any) {
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const rowData: FYPeriod = data[row.index];
  const setFYPeriod = data[row.index].setFYPeriod;
  const index: number = data[row.index].index;

  async function showDetails() {
    await showGlobalOkDialog(
      <ChangeFYPeriod index={index} fisvalYear={rowData} setFYPeriod={setFYPeriod} />,
      true
    );
  }

  return (
    <>
      <div style={{width: '100%', textAlign: 'center', fontWeight: 'bold', color: 'blue', cursor: 'pointer'}} onClick={showDetails}>
        {!rowData.yearEnd ? 'EDIT' : ''}
      </div>
    </>
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function ManageFiscalYearEndImpl({ yearEnd, refreshCnt, setRefreshCnt, onPopupClose }: ManageFiscalYearEndParams) {
  const [inStorage, setInStorage] = useTypedFastStorage<FiscalYearEnd>('manage-year-end-processing', null);
  const [delPIds, setDelPIds] = useState<Array<number>>([]);
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const keepStartDate = useRef(yearEnd.acctStartDate??'');


  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
    

  async function initialize() {
    setInStorage({...yearEnd});
  }

  async function savePeriods() {
    if (!inStorage) return;

    showProgressDialog(true);

    const sqlInfoList: Array<SQLInfo> = [];
    const startDate = inStorage.fyPeriods[inStorage.fyPeriods.length - 1].beginDate??'';

    // 1. Update Start Year/Month, FiscalMonth
    sqlInfoList.push(new SQLInfo({
      type: SQLType.Update,
      tableName: 'T_AOTHERINFO',
      fields: [
        new SQLField({ fieldName: 'F_N2', propertyName: 'N2', value: formatDate(inStorage.fyPeriods[0].endDate, 'MM')})
      ],
      where: "F_TBName = 'T_CODEDATA' And F_TBID = 1 And F_Type = 27"
    }));

    if (compareIso(keepStartDate.current, startDate) !== 0) {
      sqlInfoList[0].fields.push(new SQLField({ fieldName: 'F_C3', propertyName: 'C3', value: formatDate(startDate, 'YYYYMM') }));
    }

    // 2. Period Insert/Update
    inStorage.fyPeriods.forEach((x, i) => {
      if (x.editFlag > 0) {
        const sqlInfo = new SQLInfo({
          type: (x.id > 0 ? SQLType.Update : SQLType.Insert),
          tableName: 'T_FYPeriod',
          fields: [
            new SQLField({ fieldName: 'F_No', propertyName: 'No', value: x.fyDesc }),
            new SQLField({ fieldName: 'F_FiscalYEar', propertyName: 'FiscalYear', value: x.fiscalYear }),
            new SQLField({ fieldName: 'F_BeginDate', propertyName: 'BeginDate', value: x.beginDate }),
            new SQLField({ fieldName: 'F_EndDate', propertyName: 'EndDate', value: x.endDate }),
            new SQLField({ fieldName: 'F_YearEnd', propertyName: 'YearEnd', value: x.yearEnd }),
            new SQLField({ fieldName: 'F_U2Id', propertyName: 'U2Id', value: appContext.user?.id??'' }),
            new SQLField({ fieldName: 'F_U2Date', propertyName: 'U2Date', value: 'GetDate()' }),
          ],
        });

        if (x.id > 0) {
          sqlInfo.key = new SQLField({ fieldName: 'F_Id', propertyName: 'Id', value: x.id });
        } else {
          sqlInfo.fields.push(new SQLField({ fieldName: 'F_U1Id', propertyName: 'U1Id', value: appContext.user?.id??'' }));
          sqlInfo.fields.push(new SQLField({ fieldName: 'F_U1Date', propertyName: 'U1Date', value: 'GetDate()' }));
        }
    
        sqlInfoList.push(sqlInfo);
      }
    });

    // 3. Period Delete
    let prevId = 0;

    delPIds.sort();
    delPIds.forEach((delId, i) => {
      if (prevId !== delId) {
        sqlInfoList.push(new SQLInfo({
          type: SQLType.Delete,
          tableName: 'T_FYPeriod',
          key: new SQLField({ fieldName: "F_Id", propertyName: "Id", value: delId}),
        }));
        prevId = delId;
      }
    });

    // 4. Do Save
    const apiResult = await appContext.api.saveFiscalYearSetting(sqlInfoList, { beginDate: inStorage.fyPeriods[0].beginDate, endDate: inStorage.fyPeriods[0].endDate });
    if (!apiResult || !apiResult.success ) {
      await showGlobalOkDialog(<p>Failed to save settings for Fiscal Year</p>);
    }

    setRefreshCnt(refreshCnt + 1);
    showProgressDialog(false);
    onPopupClose();
  }

  function showProgressDialog(show: boolean) {
    const el = document.getElementById('yearend-progress-dialog');
    if (el) {
      el.style.display = show ? 'block' : 'none';
    }
  }

  if (!inStorage) {   
    return null;
  }

  return (
    <ManageFiscalYearEndInitializedImpl yearEnd={inStorage} setYearEnd={setInStorage} 
      delPeriodIds={delPIds} setDelPeriodIds={setDelPIds} savePeriods={savePeriods} onPopupClose={onPopupClose} />
  );

}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function ManageFiscalYearEndInitializedImpl({ yearEnd, setYearEnd, delPeriodIds, setDelPeriodIds, savePeriods, onPopupClose }: ManageFiscalYearEndLoadedDetailsViewParams) {
  const tableStorage = useTableStorage();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const appContext = useContext(ApplicationContext);

  function setFYPeriod(index: number, fyDesc: string, endDate: string) {
    const newPeriods = _.cloneDeep(yearEnd.fyPeriods);
    let changeLevel = 0, diffMonth = 0;
    let tempDate = endDate;

    if (newPeriods[index].fyDesc !== fyDesc) {
      changeLevel = 1;
      newPeriods[index].fyDesc = fyDesc;
      newPeriods[index].no = fyDesc;
    }

    if (newPeriods[index].endDate !== endDate) {
      diffMonth = getDiffMonths(newPeriods[index].endDate??'', endDate);
      if (diffMonth !== 0) {
        changeLevel = 2;
        newPeriods[index].endDate = endDate;
        newPeriods[index].year = +formatDate(endDate, 'YYYY');
        newPeriods[index].fiscalYear = +formatDate(endDate, 'YYYYMM');
      }
    }

    newPeriods[index].editFlag = changeLevel;

    if (changeLevel === 2 && diffMonth !== 0) {
      for (let k = index - 1; k >= 0; k--) {
        tempDate = addDays(tempDate, 1);
        newPeriods[k].beginDate = tempDate;

        tempDate = addDays(addYears(tempDate, 1), -1);
        newPeriods[k].endDate = tempDate;
        
        newPeriods[k].year = +formatDate(tempDate, 'YYYY');
        newPeriods[k].fiscalYear = +formatDate(tempDate, 'YYYYMM');
        newPeriods[k].editFlag = 2;
      }
    }

    setYearEnd({fyPeriods: newPeriods});
  }

  function setStartDate(startYear: string, startMonth: string) {
    const newPeriods = _.cloneDeep(yearEnd.fyPeriods);
    const delIds = _.cloneDeep(delPeriodIds);
    const newStartDate = startYear + '-' + startMonth + '-01';

    if (compareIso(newStartDate, newPeriods[0].endDate??'') > 0) {
      showGlobalOkDialog(<p>The input date is larger than the last date currently set.</p>);
      return false;
    }

    if (compareIso(newStartDate, yearEnd.acctStartDate ?? '') === 0) {
      return false;
    }

    let lastPeriod = newPeriods[newPeriods.length - 1];
    let firstBeginDate = lastPeriod.beginDate??'';
    let firstEndDate = lastPeriod.endDate??'';
    let firstfyDesc = lastPeriod.fyDesc;

    do {
      // newDate < firstBeginDate
      if (compareIso(newStartDate, firstBeginDate) < 0) {
        const newPeriod = new FYPeriod();

        firstfyDesc = getPreviousNo(firstfyDesc);
        firstEndDate = addDays(firstBeginDate, -1);
        firstBeginDate = addYears(firstBeginDate, -1);

        newPeriod.fyDesc = firstfyDesc;
        newPeriod.no = firstfyDesc;
        newPeriod.beginDate = firstBeginDate;
        newPeriod.endDate = firstEndDate;
        newPeriod.year = +formatDate(firstEndDate, 'YYYY');
        newPeriod.fiscalYear = +formatDate(firstEndDate, 'YYYYMM');;
        newPeriod.endDate = firstEndDate;
        newPeriod.action = "EDIT";
        newPeriod.editFlag = 2;

        newPeriods.push(newPeriod);
        lastPeriod = newPeriods[newPeriods.length - 1];

      // firstBeginDate < newDate <= firstEndDate
      } else if (compareIso(firstBeginDate, newStartDate) < 0 && compareIso(newStartDate, firstEndDate) <= 0) {
        lastPeriod.beginDate = newStartDate;
        lastPeriod.editFlag = 2;
        firstBeginDate = newStartDate;

      // firstEndDate < newDate
      } else {
        if (lastPeriod.id > 0) {
          //const currIds = _.cloneDeep(delPeriodIds);
          delIds.push(lastPeriod.id );
          //setDelPeriodIds(currIds);
        }

        newPeriods.pop();

        lastPeriod = newPeriods[newPeriods.length - 1];
        firstBeginDate = lastPeriod.beginDate??'';
        firstEndDate = lastPeriod.endDate??'';
      }
    } while (compareIso(newStartDate, firstBeginDate) !== 0);

    setDelPeriodIds(delIds);
    setYearEnd({fyPeriods: newPeriods});
  }

  function getPreviousNo(fyDesc: string) {
    let newNo = '', prefix = '';
    let k = -1;

    for (k = fyDesc.length - 1; k >= 0; k--) {
      if (!('0' <= fyDesc[k] && fyDesc[k] <= '9')) break;
    }

    if (fyDesc.length > (k + 1) && k >= 0) {
      newNo = (Number(fyDesc.slice(k + 1)) - 1).toString();
      prefix = fyDesc.slice(0, k + 1);
    }

    return prefix + newNo;
  }

  async function showSelectStartDate() {
    const apiResult = await appContext.api.beginJournalCount();
    if (!apiResult || !apiResult.success ) {
      await showGlobalOkDialog(<p>Failed to read the Beginning Journal.</p>);
      return;
    }

    if (apiResult.result > 0) {
      await showGlobalOkDialog(<p>Account Start Date cannot be changed because Beginning Journal exists.</p>);
      return;
    }

    await showGlobalOkDialog(
      <ChangeStartDate startDate={yearEnd.acctStartDate??''} setStartDate={setStartDate} />,
      true
    );
  }

  const yearEndProcessingSettings: ApplicableTableSettings = {
    tableName: 'fiscalYearSettingsDetailsTable',
    ...fiscalYearSettingsDetailsTable,
    data: yearEnd.fyPeriods.map((x, i) => ({...x, index: i, setFYPeriod })),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'fyDesc', Cell: TextRenderer },
      { accessor: 'beginDate', Cell: DateRenderer },
      { accessor: 'endDate', Cell: DateRenderer },
      { accessor: 'yearEnd', Cell: NullableCheckboxIconRenderer },
      { accessor: 'action', Cell: ActionRenderer },
      { accessor: 'u2Date', Cell: DateTimeRenderer },
    ],
    customStorage: tableStorage,
    headersColor: 'white',
    footerRow: [ 
    ],
    height: '220px',
    rowProps: (row: any) => {
      if (yearEnd.fyPeriods[row.index].editFlag) {
          return {
            style: { backgroundColor: 'rgb(160, 225, 160)' }
          };
      }

      return undefined;
    },    
  };

  return (
    <div className='report-popup'>
      <Fieldset label="Fiscal Year Settings">
        <div style={{display: 'flex', marginTop: '5px'}}>
          <div style={{width: '200px', marginLeft: '30px'}}>
            <InlineInputControlRow label="Fiscal Year Settings" smallMargin mode="smaller">
              <TextBox name='year-end-manage-fiscalMonthDesc' disabled value={yearEnd.fiscalMonthDesc} style={{textAlign: 'center'}} />
            </InlineInputControlRow>

            <InlineInputControlRow label="Start Date" smallMargin mode="smaller">
              <TextBox name='year-end-manage-acctStartDate' disabled value={formatDate(yearEnd.acctStartDate, "YYYY.MM")} style={{textAlign: 'center'}} />
            </InlineInputControlRow>
          </div>

          <div style={{width: '160px', marginLeft: '70px', alignSelf: 'center'}}>
            <PrimaryButton name='year-end-manage-change-button' disabled={yearEnd.fyPeriods.filter((x) => x.yearEnd).length} size="regular" style={{width: '100%'}} onClick={() => showSelectStartDate()} >Change Start Date</PrimaryButton>
          </div>

        </div>
      </Fieldset>

      <div style={{marginLeft: '30px', marginTop: '20px', }}>
        <Label label="Fiscal Year End Processing" bold />
      </div>
      <div>
        <SmartTable {...yearEndProcessingSettings} />
      </div>

      <div className='report-footer'>
        <SecondaryButton
          name='year-end-manage-apply-button'
          style={{width: '90px'}}
          size="regular"
          onClick={savePeriods}
        >
          APPLY
        </SecondaryButton>
        <SecondaryButton
          name='year-end-manage-detail-cancel-button'
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>

      <Portal node={document.body}>
        <div id="yearend-progress-dialog" style={ProgressDialogStyle}>
          <div style={ProgressDialogContainerStyle}>
            <div style={{display: 'flex'}}>
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div><strong>Please wait while processing...</strong></div>
                {/* <div id="yearend-progress-counter"></div> */}
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function ChangeFYPeriod({index, fisvalYear, setFYPeriod}:{index: number, fisvalYear: FYPeriod, setFYPeriod: (index: number, fyDesc: string, endDate: string) => void}) {
  const globalOkDialog = useAppSelector(state => state.globalOkDialog);
  const dispatch = useAppDispatch();
  const [endList, setEndList] = useState<Array<string>>([]);
  const [fyData, setFyData] = useState<FYPeriod>(fisvalYear);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function initialize() {
    if (endList.length <= 0) {
      const ListData: Array<string> = [];

      let tempDate = addYears(fisvalYear.beginDate??'', 1);

      for (let k = 1; k <= 12; k++) {
        ListData.push(formatDate(addDays(tempDate, -1), 'MM-DD-YYYY'));
        tempDate = addMonths(tempDate, -1);
        if (compareIso(fisvalYear.beginDate??'', tempDate) > 0) break;
      }

      setEndList(ListData);
      setFyData({...fyData, endDate: formatDate(fisvalYear.endDate, 'MM-DD-YYYY')})
    }
  }

  function closeDialog() {
    if (globalOkDialog.resolve) {
      globalOkDialog.resolve();
    }
    dispatch(hideGlobalOkDialog());
  }

  function onOk(){
    setFYPeriod(index, fyData.fyDesc, fyData.endDate??'');
    closeDialog();
  }

  return (
    <div style={{display: 'flex', width: '380px'}}>
      <div style={{width: '80px'}}>
        <div>
          <Label label="Fiscal Year" />
        </div>
        <div style={{position: 'relative', top: '-8px'}}>
          <TextBox name='year-end-manage-fyDesc' value={fyData.fyDesc} onChange={e => setFyData({...fyData, fyDesc: e.target.value})} />
        </div>
      </div>
      <div style={{marginLeft: '20px', width: '80px'}}>
        <div>
          <Label label="Begin Date" />
        </div>
        <div style={{position: 'relative', top: '-8px'}}>
          <Date name='year-end-manage-beginDate' disabled value={fyData.beginDate} hideIcon />
        </div>
      </div>
      <div style={{marginLeft: '20px', width: '100px'}}>
        <div>
          <Label label="End Date" />
        </div>
        <div style={{position: 'relative', top: '-3px', fontSize: '10px'}}>
          <Select name='year-end-manage-endDate' value={fyData.endDate??''} 
            onChange={e => {setFyData({...fyData, endDate: e.target.value})}}
            items={endList.map(x=>({label: x, value: x}))} />
        </div>
      </div>
      <div style={{marginLeft: '20px', width: '80px'}}>
        <div style={{height: '30px'}}>
          <SecondaryButton name='onok-year-end-manage-cancel-button' style={{width: '100%'}} size="small" onClick={closeDialog}>Cancel</SecondaryButton>
        </div>

        <div style={{height: '30px'}}>
          <SecondaryButton name='onok-year-end-manage-ok-button' style={{width: '100%'}} size="small" onClick={onOk}>Ok</SecondaryButton>
        </div>
      </div>
    </div>
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function ChangeStartDate({startDate, setStartDate}:{startDate: string, setStartDate: (startYear: string, startMonth: string) => void }) {
  const globalOkDialog = useAppSelector(state => state.globalOkDialog);
  const dispatch = useAppDispatch();
  const [startYear, setStartYear] = useState('');
  const [startMonth, setstarMonth] = useState('');
  const monthList = ['01','02','03','04','05','06','07','08','09','10','11','12'];
  const [yearList, setYearList] = useState<Array<string>>([]);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function initialize() {
    if (yearList.length <= 0) {
      const temp: Array<string> = [];
      const year1 = +formatDate(startDate, 'YYYY');
      const year2 = getCurrentYear() + 1;

      for (let k = year1 - 10; k <= year2; k++) {
        temp.push('' + k);
      }

      setYearList(temp);
    }

    setStartYear(formatDate(startDate, 'YYYY'));
    setstarMonth(formatDate(startDate, 'MM'));
  }

  function closeDialog() {
    if (globalOkDialog.resolve) {
      globalOkDialog.resolve();
    }
    dispatch(hideGlobalOkDialog());
  }

  function onOk(){
    setStartDate(startYear, startMonth);
    closeDialog();
  }

  return (
    <div style={{display: 'flex', width: '380px'}}>
      <div style={{display: 'inline', width: '270px'}}>
        <p style={{fontSize: '13px'}}>Select Year and Month for the Start Date</p>
        <div style={{display: 'flex', width: '270px'}}>
        <div style={{width: '100px', position: 'relative', top: '-3px', fontSize: '10px'}}>
          <Select name='year-end-manage-startYear' value={startYear} 
            onChange={e => {setStartYear(e.target.value)}}
            items={yearList.map(x=>({label: x, value: x}))} />
        </div>
        <div style={{marginLeft: '30px', width: '60px', position: 'relative', top: '-3px', fontSize: '10px'}}>
          <Select name='year-end-manage-startMonth' value={startMonth} 
            onChange={e => {setstarMonth(e.target.value)}}
            items={monthList.map(x=>({label: x, value: x}))} />
        </div>
      </div>
      </div>

      <div style={{marginLeft: '20px', width: '80px'}}>
        <div style={{height: '30px'}}>
          <SecondaryButton name='year-end-manage-cancel-button' style={{width: '100%'}} size="small" onClick={closeDialog}>Cancel</SecondaryButton>
        </div>
        <div style={{height: '30px'}}>
          <SecondaryButton name='year-end-manage-ok-button' style={{width: '100%'}} size="small" onClick={onOk}>Ok</SecondaryButton>
        </div>
      </div>
    </div>
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const ManageFiscalYearEnd = ({yearEnd, refreshCnt, setRefreshCnt, onPopupClose}: ManageFiscalYearEndParams) => {
  return (
    <ManageFiscalYearEndImpl  yearEnd={yearEnd} refreshCnt={refreshCnt} setRefreshCnt={setRefreshCnt} onPopupClose={onPopupClose} key={`manageFiscalYearEnd-details`}/>
  );
};
export default ManageFiscalYearEnd;
